import { useEffect, useState } from "react";
import { Badge, Button, ButtonVariant, Text, TextField } from "@merge-api/merge-javascript-shared";
import { Search } from "lucide-react";
import { Dialog } from "@merge-api/merge-javascript-shared/dist/designSystem/molecules/Dialog";
import { showErrorToast, showSuccessToast } from "../../../shared/Toasts";
import { conductorDelete, conductorPut } from "../../conductorApi";
import { useConductorResource } from "../../hooks/useConductorResource";
import { Select } from "../../components/ConductorFormFields";
import { OrganizationSyncSpeed, SPEED_OPTIONS, speedLabel } from "../types";
import SpeedField from "./SpeedField";

// The org's speed row: the entitlement merge-backend resolves from billing plus
// the admin override on top, the same two layers the account card shows. The
// override covers every account in the org without its own, including ones
// linked later.
interface Props {
  /** The organization of the account being viewed, when there is one. */
  prefillOrganizationId?: string;
  /** Called after a write so the account read can pick up the new org layer. */
  onOverrideChanged?: () => void;
}

const OrganizationOverrideCard = ({ prefillOrganizationId, onOverrideChanged }: Props) => {
  // state
  const [orgInput, setOrgInput] = useState(prefillOrganizationId ?? "");
  const [organizationId, setOrganizationId] = useState(prefillOrganizationId ?? "");
  const [reloadToken, setReloadToken] = useState(0);
  const [speed, setSpeed] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  // hooks
  // A 404 means the org has no row at all, which is a normal state for an org
  // backend has not written an entitlement for, so no error toast.
  const { data: orgSpeed } = useConductorResource<OrganizationSyncSpeed>(
    organizationId ? `/conductor/organization-sync-speeds/${organizationId}` : null,
    undefined,
    reloadToken
  );

  // Follow the account being viewed, while staying independently searchable so
  // an org override can be reached without a linked account id.
  useEffect(() => {
    if (prefillOrganizationId) {
      setOrgInput(prefillOrganizationId);
      setOrganizationId(prefillOrganizationId);
    }
  }, [prefillOrganizationId]);

  // Seed the picker from the current override, or leave it blank when there is
  // none, so an unset override never looks like a chosen one.
  useEffect(() => {
    setSpeed(orgSpeed?.speed_override ?? "");
  }, [orgSpeed]);

  // derived state
  const override = orgSpeed?.speed_override ?? null;

  // event handlers
  const save = async () => {
    setIsSaving(true);
    const { ok, error } = await conductorPut(
      `/conductor/organization-sync-speeds/${organizationId}/speed-override`,
      {
        speed,
      }
    );
    setIsSaving(false);
    if (ok) {
      showSuccessToast("Organization override saved");
      setReloadToken((token) => token + 1);
      onOverrideChanged?.();
    } else {
      showErrorToast(error || "Failed to save organization override");
    }
  };

  const clear = async () => {
    setIsSaving(true);
    // A 204, unlike the per-account clear: there is no reverted row to read, so
    // the fallback comes from the refetch below.
    const { ok, error } = await conductorDelete(
      `/conductor/organization-sync-speeds/${organizationId}/speed-override`
    );
    setIsSaving(false);
    setConfirmClear(false);
    if (ok) {
      showSuccessToast("Organization override cleared");
      setReloadToken((token) => token + 1);
      onOverrideChanged?.();
    } else {
      showErrorToast(error || "Failed to clear organization override");
    }
  };

  // render
  return (
    <div className="h-full rounded-lg border border-gray-10 bg-white p-4">
      <Text variant="h6">Organization speed</Text>
      <Text variant="sm" className="mt-1 block text-gray-60">
        Covers accounts in the organization with no override of their own, including ones linked
        later. Follows the account on the left, or search any organization.
      </Text>

      <div className="mt-4">
        <Text variant="sm" className="mb-1 block text-gray-60">
          Organization ID
        </Text>
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <TextField
              value={orgInput}
              onChange={(event) => setOrgInput(event.target.value)}
              placeholder="paste an organization UUID"
            />
          </div>
          <Button
            size="md"
            leftIcon={<Search size={16} />}
            disabled={orgInput.trim() === ""}
            onClick={() => setOrganizationId(orgInput.trim())}
          >
            Look up
          </Button>
        </div>
      </div>

      {organizationId && (
        <>
          <div className="mt-4 border-t border-gray-10 pt-4">
            <div className="flex items-center gap-2">
              <Text variant="sm" className="text-gray-60">
                Effective
              </Text>
              {orgSpeed ? (
                <Badge color={override ? "orange" : "gray"}>
                  {speedLabel(orgSpeed.effective_speed)}
                </Badge>
              ) : (
                <Badge color="gray">No row</Badge>
              )}
            </div>

            {/* The two writers on the row, so it is clear which one is answering. */}
            <div className="mt-3 flex gap-4">
              <div className="flex-1">
                <SpeedField label="Override">
                  <Text variant="md" className={override ? "text-gray-80" : "text-gray-40"}>
                    {override ? speedLabel(override) : "None"}
                  </Text>
                </SpeedField>
              </div>
              <div className="flex-1">
                <SpeedField label="Entitlement">
                  <Text variant="md" className={orgSpeed?.speed ? "text-gray-80" : "text-gray-40"}>
                    {orgSpeed?.speed ? speedLabel(orgSpeed.speed) : "None"}
                  </Text>
                </SpeedField>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-2">
            <div className="w-40">
              <Text variant="sm" className="mb-1 block text-gray-60">
                Speed
              </Text>
              <Select value={speed} onChange={setSpeed} disabled={isSaving}>
                <option value="">Select a speed…</option>
                {SPEED_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <Button
              size="md"
              onClick={save}
              disabled={isSaving || speed === "" || speed === override}
            >
              {override ? "Save override" : "Set override"}
            </Button>
            <Button
              size="md"
              variant={ButtonVariant.SecondaryGray}
              disabled={isSaving || !override}
              onClick={() => setConfirmClear(true)}
            >
              Clear org override
            </Button>
          </div>
        </>
      )}

      <Dialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        variant="sm"
        title="Clear organization override?"
        primaryButtonText="Clear override"
        primaryButtonVariant={ButtonVariant.DangerFilled}
        primaryButtonLoading={isSaving}
        onPrimaryButtonClick={clear}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setConfirmClear(false)}
      >
        <Text variant="md" className="text-gray-70">
          {orgSpeed?.speed
            ? `Every account in this organization without its own override falls back to ${speedLabel(
                orgSpeed.speed
              )}, the entitlement conductor resolves from billing.`
            : "This organization has no billing entitlement recorded, so clearing the override removes its speed row entirely."}
        </Text>
      </Dialog>
    </div>
  );
};

export default OrganizationOverrideCard;
